import { faker } from '@faker-js/faker/locale/en'
import type { DateTime } from 'luxon'
import type {
  ContainerType,
  ContainerWatchState,
  GETWatch,
  PortComplex,
  ShippingLine,
  SimpleUser,
  TerminalName,
} from '~/services/apiClient'
import { fastParseISO, shortenContainerType } from '~/utils'

export class ContainerWatch {
  container_number: string
  state: ContainerWatchState
  shipping_line: ShippingLine | null
  container_type: ContainerType | null
  type_short: string | undefined
  customer_id: number | null
  customer_name: string | null
  terminal: TerminalName | null
  import_last_free_date: DateTime | null
  /**
   * The line's own LFD, where we have one from any source. Distinct from
   * `import_last_free_date`, which is the earlier of the terminal's and the line's
   * and is what the board displays; this one only answers whether the line's date is
   * known at all, which is what decides the "check line for LFD" caveat.
   */
  import_line_last_free_date: DateTime | null
  tags: string[] = []
  import_appointment_pickup_number: string | null
  tagsSet: Set<string> = new Set()
  assignedUsers: SimpleUser[] = []
  assignedUsersSet: Set<SimpleUser> = new Set()
  port_complex: PortComplex
  constructor(watch: GETWatch, demoMode: boolean) {
    this.container_number = watch.container_number
    this.state = watch.state
    this.shipping_line = watch.shipping_line ?? null
    this.container_type = watch.container_type ?? null
    this.customer_id = null
    this.customer_name = null
    this.type_short = shortenContainerType(this.container_type || undefined)
    this.import_appointment_pickup_number =
      watch.import_appointment_pickup_number ?? null
    this.terminal = watch.last_related_terminal ?? null
    this.import_last_free_date = watch.import_last_free_date
      ? fastParseISO(watch.import_last_free_date)
      : null
    this.import_line_last_free_date = watch.import_line_last_free_date
      ? fastParseISO(watch.import_line_last_free_date)
      : null
    if (watch.customer) {
      this.customer_id = watch.customer.id
      if (demoMode) {
        this.customer_name = faker.company.name()
      } else {
        this.customer_name = watch.customer.name
      }
    }
    if (demoMode && watch.tags.length > 0) {
      this.tags = [faker.commerce.productName()]
    } else {
      this.tags = Array.isArray(watch.tags) ? watch.tags : []
    }
    this.tagsSet = new Set(this.tags)
    this.assignedUsers = watch.assigned_users
    this.assignedUsersSet = new Set(this.assignedUsers)
    this.port_complex = watch.port_complex
  }

  // An alias to make this compatible with the Container interface
  get assigned_users(): SimpleUser[] {
    return this.assignedUsers
  }

  get type(): ContainerType | undefined {
    return this.container_type || undefined // Alias
  }

  get line(): ShippingLine | undefined {
    return this.shipping_line || undefined // Alias
  }

  get cycle_state(): ContainerWatchState {
    return this.state
  }

  get number(): string {
    return this.container_number
  }
}
